import produce from "immer"
import { ASC } from "const"
import { InferValueTypes } from "client/utils"
import { createLifecyclePermissions } from "client/store/utils"
import * as types from "./types"
import * as actions from "./actions"

type ActionTypes = ReturnType<InferValueTypes<typeof actions>>

const initLifecyclePermissionsState: types.LifecyclePermissionsState = {
    lifecyclePermissions: createLifecyclePermissions().permissionsSectioned,

    accountGroups: {
        groupIds: null,
        sortColumn: "name",
        sortOrder: ASC,
        filters: {
            search: null,
        },
        selected: null,
    },
    projectGroups: {
        groupIds: null,
        sortColumn: "name",
        sortOrder: ASC,
        filters: {
            search: null,
        },
        selected: null,
    },
    users: {
        userIds: null,
        sortColumn: "firstName",
        sortOrder: ASC,
        filters: {
            status: null,
            search: null,
        },
        selected: null,
    },

    reloadList: true,
}

const lifecyclePermissionsReducer = produce((
    draft: types.LifecyclePermissionsState = initLifecyclePermissionsState,
    action: ActionTypes = null,
): types.LifecyclePermissionsState => {
    switch (action.type) {
        case types.SET_LIFECYCLE_PERMISSIONS:
            draft.lifecyclePermissions = action.lifecyclePermissions
            return draft

        case types.UPDATE_LIFECYCLE_PERMISSION: {
            const { section, permissionId, type, value } = action.data
            if (draft.lifecyclePermissions[section]
                && draft.lifecyclePermissions[section][permissionId]
                && draft.lifecyclePermissions[section][permissionId][type] !== null) {
                draft.lifecyclePermissions[section][permissionId][type] = value
            }
            return draft
        }

        case types.TOGGLE_LIFECYCLE_PERMISSION_CUSTOMIZABLE: {
            const { section, permissionId } = action
            const permission = draft.lifecyclePermissions[section] && draft.lifecyclePermissions[section][permissionId]
                ? draft.lifecyclePermissions[section][permissionId]
                : null
            if (permission) {
                draft.lifecyclePermissions[section][permissionId].customizable = !permission.customizable
            }
            return draft
        }

        // not assigned account groups
        case types.LIFECYCLE_PERMISSIONS_ACCOUNT_GROUP_IDS:
            draft.accountGroups.groupIds = action.groupIds
            return draft

        case types.LIFECYCLE_PERMISSIONS_ACCOUNT_GROUP_SORT:
            draft.accountGroups.sortColumn = action.sortColumn
            draft.accountGroups.sortOrder = action.sortOrder
            return draft

        case types.LIFECYCLE_PERMISSIONS_ACCOUNT_GROUP_FILTERS:
            draft.accountGroups.filters = action.filters
            return draft

        case types.LIFECYCLE_PERMISSIONS_ACCOUNT_GROUP_SELECTED:
            draft.accountGroups.selected = action.selected
            return draft

        // not assigned project groups
        case types.LIFECYCLE_PERMISSIONS_PROJECT_GROUP_IDS:
            draft.projectGroups.groupIds = action.groupIds
            return draft

        case types.LIFECYCLE_PERMISSIONS_PROJECT_GROUP_SORT:
            draft.projectGroups.sortColumn = action.sortColumn
            draft.projectGroups.sortOrder = action.sortOrder
            return draft

        case types.LIFECYCLE_PERMISSIONS_PROJECT_GROUP_FILTERS:
            draft.projectGroups.filters = action.filters
            return draft

        case types.LIFECYCLE_PERMISSIONS_PROJECT_GROUP_SELECTED:
            draft.projectGroups.selected = action.selected
            return draft

        // not assigned users
        case types.LIFECYCLE_PERMISSIONS_USER_IDS:
            draft.users.userIds = action.userIds
            return draft

        case types.LIFECYCLE_PERMISSIONS_USER_SORT:
            draft.users.sortColumn = action.sortColumn
            draft.users.sortOrder = action.sortOrder
            return draft

        case types.LIFECYCLE_PERMISSIONS_USER_FILTERS:
            draft.users.filters = action.filters
            return draft

        case types.LIFECYCLE_PERMISSIONS_USER_SELECTED:
            draft.users.selected = action.selected
            return draft

        // reload state
        case types.LIFECYCLE_PERMISSIONS_RELOAD_LIST:
            draft.reloadList = action.value
            return draft

        default:
            return draft
    }
})
export default lifecyclePermissionsReducer
